import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, AlertTriangle } from "lucide-react";
import Reveal from "./Reveal.jsx";
import { BRAND } from "../theme.js";

const initial = { name: "", email: "", company: "", message: "", budget: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorInfo, setErrorInfo] = useState(null);

  function validate() {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = "Ange ditt namn.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Ange en giltig e-postadress.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Skriv minst 10 tecken om ditt projekt.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    setErrorInfo(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const contentType = res.headers.get("content-type") || "";
      const rawBody = await res.text();
      let parsed = null;
      if (contentType.includes("application/json")) {
        try {
          parsed = JSON.parse(rawBody);
        } catch {
          /* leave parsed null */
        }
      }

      if (!res.ok) {
        // Build a detailed error payload to render in the UI
        setErrorInfo({
          status: res.status,
          statusText: res.statusText,
          message: parsed?.error || parsed?.message || null,
          detail: parsed?.detail || null,
          rawBody: !parsed ? rawBody.slice(0, 800) : null,
        });
        setStatus("error");
        // Also log to console for inspection (Network tab has the full response)
        console.error("Kontaktformulär — fel från /api/contact:", {
          status: res.status,
          statusText: res.statusText,
          contentType,
          body: parsed ?? rawBody,
        });
        return;
      }

      setStatus("success");
      setForm(initial);
    } catch (err) {
      setErrorInfo({
        status: 0,
        statusText: "Nätverksfel",
        message: err?.message || "Kunde inte nå servern (fetch failed).",
        detail:
          "Kontrollera internetuppkoppling, eller att Cloudflare Pages Function är driftsatt. Lokalt med 'npm run dev' körs inte /api/contact — använd 'npx wrangler pages dev' eller deploya först.",
      });
      setStatus("error");
      console.error("Kontaktformulär — nätverksfel:", err);
    }
  }

  function update(k) {
    return (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));
  }

  function reset() {
    setStatus("idle");
    setForm(initial);
    setErrors({});
    setErrorInfo(null);
  }

  useEffect(() => {
    if (status !== "success") return;
    const t = setTimeout(reset, 5000);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <section id="kontakt" className="relative bg-sand-50 py-24 sm:py-32">
      <div className="container-x">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-leather" />
                Kontakt
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="section-title mt-5">
                Berätta om ditt <span className="text-leather">projekt.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-lg leading-relaxed text-navy-700">
                Vi svarar inom 24 timmar med konkreta nästa steg. Inget skämt —
                ingen säljpitch, bara raka besked.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              {[
                { icon: Mail, label: BRAND.email, href: `mailto:${BRAND.email}` },
                { icon: Phone, label: BRAND.phone, href: `tel:${BRAND.phone.replace(/\s/g, "")}` },
                { icon: MapPin, label: `${BRAND.city} · ${BRAND.region}` },
              ].map(({ icon: Icon, label, href }) => (
                <Reveal key={label}>
                  <a
                    href={href || "#"}
                    className="flex items-center gap-4 rounded-2xl border border-navy-900/8 bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-soft"
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-navy-900 text-sand-200">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-medium text-navy-900">{label}</span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              noValidate
              className="relative overflow-hidden rounded-3xl border border-navy-900/8 bg-white p-7 shadow-soft sm:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  id="contact-name"
                  label="Namn"
                  required
                  value={form.name}
                  onChange={update("name")}
                  error={errors.name}
                  placeholder="Ditt namn"
                  autoComplete="name"
                />
                <Field
                  id="contact-email"
                  label="E-post"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  error={errors.email}
                  placeholder="namn@företag.se"
                  autoComplete="email"
                />
                <Field
                  id="contact-company"
                  label="Företag"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Företagsnamn (valfritt)"
                  autoComplete="organization"
                />
                <div>
                  <label
                    htmlFor="contact-budget"
                    className="mb-2 block text-sm font-semibold text-navy-900"
                  >
                    Budget
                  </label>
                  <select
                    id="contact-budget"
                    name="budget"
                    value={form.budget}
                    onChange={update("budget")}
                    className="w-full appearance-none rounded-2xl border border-navy-900/15 bg-white px-4 py-3 text-sm text-navy-900 outline-none transition focus:border-navy-900 focus:ring-4 focus:ring-navy-900/10"
                  >
                    <option value="">Välj nivå</option>
                    <option>Under 25 000 kr</option>
                    <option>25 000 – 75 000 kr</option>
                    <option>75 000 – 150 000 kr</option>
                    <option>Över 150 000 kr</option>
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="contact-message"
                  className="mb-2 block text-sm font-semibold text-navy-900"
                >
                  Om projektet <span className="text-leather">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Berätta kort om mål, tidsram och vad du behöver hjälp med."
                  className={`w-full rounded-2xl border px-4 py-3 text-sm text-navy-900 outline-none transition focus:ring-4 ${
                    errors.message
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
                      : "border-navy-900/15 focus:border-navy-900 focus:ring-navy-900/10"
                  }`}
                />
                {errors.message && (
                  <p className="mt-1.5 text-xs text-red-600">{errors.message}</p>
                )}
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-navy-600">
                  Genom att skicka godkänner du vår behandling av dina
                  uppgifter enligt GDPR.
                </p>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary disabled:opacity-60"
                >
                  {status === "sending" ? "Skickar…" : "Skicka meddelande"}
                  <Send className="h-4 w-4" />
                </button>
              </div>

              <AnimatePresence>
                {status === "success" && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.15 }}
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-white/70 backdrop-blur-sm p-8 text-center"
                  >
                    <button
                      type="button"
                      onClick={reset}
                      className="absolute top-4 right-4 size-9 rounded-full bg-navy-900/8 hover:bg-navy-900/15 grid place-items-center transition-colors"
                      aria-label="Stäng och skicka ny förfrågan"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>

                    <motion.div
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.25 }}
                      className="size-20 rounded-full bg-emerald-500 border-4 border-sand-100 shadow-soft grid place-items-center text-4xl text-white"
                    >
                      ✓
                    </motion.div>
                    <motion.div
                      initial={{ y: 12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.35 }}
                    >
                      <p className="font-display text-2xl sm:text-3xl font-semibold text-navy-900">
                        Tack, din förfrågan är inskickad!
                      </p>
                      <p className="mt-2 text-navy-600 text-base">Vi hör av oss inom 24 timmar.</p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
              {status === "error" && (
                <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">
                        Något gick fel — maila oss gärna direkt på{" "}
                        <a
                          href={`mailto:${BRAND.email}`}
                          className="underline"
                        >
                          {BRAND.email}
                        </a>
                        .
                      </p>

                      {errorInfo && (
                        <div className="mt-3 space-y-1.5 rounded-xl border border-amber-200 bg-white/70 p-3 font-mono text-xs leading-relaxed text-amber-900">
                          <div>
                            <span className="text-amber-700">Status:</span>{" "}
                            <strong>
                              {errorInfo.status}{" "}
                              {errorInfo.statusText || ""}
                            </strong>
                          </div>
                          {errorInfo.message && (
                            <div className="break-words">
                              <span className="text-amber-700">Meddelande:</span>{" "}
                              {errorInfo.message}
                            </div>
                          )}
                          {errorInfo.detail && (
                            <div className="whitespace-pre-wrap break-words">
                              <span className="text-amber-700">Detalj:</span>{" "}
                              {errorInfo.detail}
                            </div>
                          )}
                          {errorInfo.rawBody && (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-amber-700 hover:text-amber-900">
                                Visa råsvar
                              </summary>
                              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px]">
                                {errorInfo.rawBody}
                              </pre>
                            </details>
                          )}
                          <p className="pt-1 text-[11px] text-amber-700">
                            Tips: Öppna webbläsarens konsol (F12) för full logg,
                            eller fliken Network och titta på{" "}
                            <code>/api/contact</code>.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, required, error, ...props }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-semibold text-navy-900"
      >
        {label}
        {required && <span className="text-leather"> *</span>}
      </label>
      <input
        id={id}
        name={id}
        required={required}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...props}
        className={`w-full rounded-2xl border px-4 py-3 text-sm text-navy-900 outline-none transition focus:ring-4 ${
          error
            ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
            : "border-navy-900/15 focus:border-navy-900 focus:ring-navy-900/10"
        }`}
      />
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
